import type { RemoteData } from '../async-actions';
import { createFetchAction } from '../async-actions';
import { getSession } from '../helpers/Session';
import { toastr } from '../helpers/Toastr';
import * as Hub from '../remote/hubs/Web_Controllers_V2_CustomersController';

// ======
// State
// ======

export interface CustomerNotesPageViewModel {
	customerName: string;
	customerNote: string;
	customerNoteInput: string;
	customerId: number;
}

export interface CustomerNotesPageState {
	viewModel: RemoteData<CustomerNotesPageViewModel, string[]>;
}

export function initialState(): CustomerNotesPageState {
	return {
		viewModel: {
			type: 'initial',
		},
	};
}

export function CustomerNotesPageReducer(
	state: CustomerNotesPageState = initialState(),
	action: CustomerNotesAction
): CustomerNotesPageState {
	switch (action.type) {
		case '@customers:CUSTOMER_NOTES_PAGE_NOTE_SAVED_SUCCESS': {
			return state;
		}
		case '@customers:CUSTOMER_NOTES_PAGE_LOADED_PENDING': {
			return {
				...state,
				viewModel: {
					type: 'loading',
				},
			};
		}
		case '@customers:CUSTOMER_NOTES_PAGE_LOADED_FAILURE': {
			return {
				...state,
				viewModel: {
					type: 'failure',
					err: action.errors,
				},
			};
		}
		case '@customers:CUSTOMER_NOTES_PAGE_LOADED_SUCCESS': {
			return {
				...state,
				viewModel: {
					type: 'success',
					data: {
						customerName: action.payload.customerName,
						customerNote: action.payload.note.customerNote,
						customerNoteInput: action.payload.note.customerNote,
						customerId: action.payload.customerId,
					},
				},
			};
		}
		case '@customers:NOTE_TEXTBOX_CHANGED': {
			const currentVm = state.viewModel;

			if (currentVm.type !== 'success') {
				return state;
			}

			return {
				...state,
				viewModel: {
					type: currentVm.type,
					data: {
						...currentVm.data,
						customerNoteInput: action.payload,
					},
				},
			};
		}
	}

	return state;
}

// ========
// Actions
// ========

export type CustomerNotesAction =
	| CUSTOMER_NOTES_PAGE_LOADED_PENDING
	| CUSTOMER_NOTES_PAGE_LOADED_FAILURE
	| CUSTOMER_NOTES_PAGE_LOADED_SUCCESS
	| CUSTOMER_NOTES_PAGE_NOTE_SAVED_PENDING
	| CUSTOMER_NOTES_PAGE_NOTE_SAVED_FAILURE
	| CUSTOMER_NOTES_PAGE_NOTE_SAVED_SUCCESS
	| NOTE_TEXTBOX_CHANGED;

export interface NOTE_TEXTBOX_CHANGED {
	type: '@customers:NOTE_TEXTBOX_CHANGED';
	payload: string;
}

export function noteTexboxChanged(payload: string): NOTE_TEXTBOX_CHANGED {
	return {
		type: '@customers:NOTE_TEXTBOX_CHANGED',
		payload,
	};
}

export interface CUSTOMER_NOTES_PAGE_NOTE_SAVED_PENDING {
	type: '@customers:CUSTOMER_NOTES_PAGE_NOTE_SAVED_PENDING';
}
export interface CUSTOMER_NOTES_PAGE_NOTE_SAVED_FAILURE {
	type: '@customers:CUSTOMER_NOTES_PAGE_NOTE_SAVED_FAILURE';
	errors: string[];
}
export interface CUSTOMER_NOTES_PAGE_NOTE_SAVED_SUCCESS {
	type: '@customers:CUSTOMER_NOTES_PAGE_NOTE_SAVED_SUCCESS';
	payload: {};
}

export interface CustomerNotesSavedArgs {
	customerId: number;
	customerNote: string;
}

export const customerNoteSaved = createFetchAction<
	CustomerNotesSavedArgs,
	CUSTOMER_NOTES_PAGE_NOTE_SAVED_PENDING,
	CUSTOMER_NOTES_PAGE_NOTE_SAVED_FAILURE,
	CUSTOMER_NOTES_PAGE_NOTE_SAVED_SUCCESS
>(
	'@customers:CUSTOMER_NOTES_PAGE_NOTE_SAVED_PENDING',
	'@customers:CUSTOMER_NOTES_PAGE_NOTE_SAVED_FAILURE',
	'@customers:CUSTOMER_NOTES_PAGE_NOTE_SAVED_SUCCESS',
	async (args) => {
		const res = await Hub.UpdateCustomerNoteForCustomer(args);
		if (res.ok) {
			toastr.success({ title: 'Success!', body: 'Customer Note Saved' });
		} else {
			toastr.error({ title: 'Uh Oh!', body: 'Customer Note Failed To Save' });
		}
		return res;
	}
);

export interface CUSTOMER_NOTES_PAGE_LOADED_PENDING {
	type: '@customers:CUSTOMER_NOTES_PAGE_LOADED_PENDING';
}
export interface CUSTOMER_NOTES_PAGE_LOADED_FAILURE {
	type: '@customers:CUSTOMER_NOTES_PAGE_LOADED_FAILURE';
	errors: string[];
}
export interface CUSTOMER_NOTES_PAGE_LOADED_SUCCESS {
	type: '@customers:CUSTOMER_NOTES_PAGE_LOADED_SUCCESS';
	payload: {
		note: Hub.CustomerNoteResult;
		customerName: string;
		customerId: number;
	};
}

export const customerNotesPageLoaded = createFetchAction<
	void,
	CUSTOMER_NOTES_PAGE_LOADED_PENDING,
	CUSTOMER_NOTES_PAGE_LOADED_FAILURE,
	CUSTOMER_NOTES_PAGE_LOADED_SUCCESS
>(
	'@customers:CUSTOMER_NOTES_PAGE_LOADED_PENDING',
	'@customers:CUSTOMER_NOTES_PAGE_LOADED_FAILURE',
	'@customers:CUSTOMER_NOTES_PAGE_LOADED_SUCCESS',
	async () => {
		const session = getSession();
		const res = await Hub.GetCustomerNoteForCustomer(session.customerId);
		if (res.kind !== 'success') {
			return res;
		}
		return {
			note: res.value,
			customerName: session.customerName,
			customerId: session.customerId,
		};
	}
);
